import { analyticsPageMapping } from '@/shared/constants/other.constants'
import { RouteRecordNameGeneric } from 'vue-router'

const CLICK_TO_PROCEED_EVENT = 'click_proceed'
export class GoogleAnalyticsService {
  readonly productLabel: string

  constructor(product: string) {
    this.productLabel = product
  }
  private send(event: string, payload: Record<string, unknown> = {}) {
    if (typeof window.gtag !== 'function') {
      throw new TypeError('[QIC-Analytics] GoogleAnalyticsService called but gtag is not defined')
    }

    const patchedPayload = {
      product: this.productLabel,
      ...payload,
    }

    const args: ['event', string, any?] = ['event', event]
    if (patchedPayload) {
      args.push(patchedPayload)
    }
    window.gtag(...args)

    console.info(`[QIC-Analytics] Event ${event} is sent by GoogleAnalyticsService`)
    console.info('%c [QIC-Analytics] Event payload: ', 'background: #5927FF; color: #fff')
    console.info(JSON.stringify(patchedPayload, null, '\t'))
  }

  private objectToStringFix(object: Record<string, unknown>) {
    if (!object) return null
    const copy: any = structuredClone(object)
    for (const [key, value] of Object.entries(copy)) {
      if (typeof value === 'object' && value !== null) {
        copy[key].toString = function () {
          return JSON.stringify(this)
        }
      }
    }
    return copy
  }

  sendABTest(group: string, value: string) {
    this.send('exp_get', { group, value })
  }

  sendPopupErrorOpen(title: string, text: string, page: string, policy_id: string) {
    this.send('popup_error', { title, text, page, policy_id })
  }

  sendPolicyEvents(
    event: string,
    page: string,
    policy_id: string | string[] | null,
    start_date?: string,
    end_date?: string,
    section?: string
  ) {
    this.send(event, { page, policy_id, start_date, end_date, section })
  }

  sendInputError(
    input_field: string,
    value: string | Date | undefined,
    text: string,
    page: string,
    policy_id: string
  ) {
    this.send('input_error', { input_field, value, text, page, policy_id })
  }

  sendPolicyDownloadErrorBaseEvents(
    event: string,
    payload: {
      product: string
      title: string
      text: string
      prev_page: string
      page: string | undefined
      policy_id: string
    }
  ) {
    this.send(event, { ...payload })
  }

  sendContactUsByPolicy(event: string, position: string, page: string, policy_id: string) {
    this.send(event, { position, page, policy_id })
  }

  sendPurchaseEnd(transaction_id: Nullable<string>, total_price: string | number) {
    this.send('purchase_end', {
      product: 'mandatory_visitors',
      page: 'your_quote',
      transaction_id,
      status: 200,
      total_price,
    })
  }

  sendViewDownloadPolicy(policy_id: Nullable<string>, number_of_policies: number) {
    this.send('view_download_policy', {
      page: 'downloading_policy',
      policy_id,
      number_of_policies,
    })
  }

  sendPurchase(
    category: boolean,
    transaction_id: Nullable<string>,
    policy_id: Nullable<string>,
    total_price: Nullable<string>,
    choosen_card: Nullable<string>,
    flg_inbound_addon?: boolean,
    inbound_price?: number
  ) {
    this.send('purchase', {
      category,
      transaction_id,
      policy_id,
      total_price,
      choosen_card,
      flg_inbound_addon,
      inbound_price,
    })
  }

  sendClickProceedBySuccessPage(policy_id: Nullable<string>, flow_start: string) {
    this.send(CLICK_TO_PROCEED_EVENT, {
      page: 'downloading_policy',
      policy_id,
      flow_start,
    })
  }

  sendClickProceedByPreapprovalForm(
    page: string,
    flow_start: string,
    policyDurationDays: number,
    policyStartDate: string,
    numberOfVisitors?: number
  ) {
    // TODO: Make numberOfVisitors required after group purchase release https://qicbizdev.atlassian.net/projects/QV/versions/11068/tab/release-report-all-issues
    this.send(CLICK_TO_PROCEED_EVENT, {
      page,
      category: 'visa',
      flow_start,
      days: policyDurationDays,
      start_date: policyStartDate,
      num_visitors: numberOfVisitors,
    })
  }

  sendEventsByContactUs(
    event: string,
    page: string[] | RouteRecordNameGeneric,
    category: boolean | string
  ) {
    this.send(event, { page, category })
  }

  sendClickPrivacyPolicy(page: string) {
    this.send('click_privacy_policy', { page })
  }

  sendClickProceedByHolderInfo(
    category: boolean,
    gender: string,
    country_of_residense: string,
    flow_start: string,
    pass_id: string
  ) {
    this.send(CLICK_TO_PROCEED_EVENT, {
      page: 'holder-info',
      category,
      gender,
      country_of_residense,
      flow_start,
      pass_id,
    })
  }

  sendClickProceedBySelectDuration(
    category: boolean,
    text: string,
    days: number,
    flow_start: string
  ) {
    this.send(CLICK_TO_PROCEED_EVENT, {
      page: 'select_duration',
      category,
      text,
      days,
      flow_start,
    })
  }

  sendClickCoverage(policy_id: string) {
    this.send('click_coverage', {
      product: 'visitors',
      policy_id,
      page: 'downloading_policy',
    })
  }

  sendClickProceedByCheck(
    category: boolean,
    num_visitors: string,
    num_validated_visitors: string,
    flow_start: string
  ) {
    this.send(CLICK_TO_PROCEED_EVENT, {
      page: 'check',
      category,
      num_visitors,
      num_validated_visitors,
      flow_start,
    })
  }

  sendAddPerson(
    page: string,
    category: boolean,
    num_visitors: number,
    quote_id: string,
    flow_start: string
  ) {
    this.send('add_person', {
      page,
      category,
      num_visitors,
      quote_id,
      flow_start,
    })
  }

  sendEventOnChangePerson(
    event: string,
    category: boolean,
    page: string,
    nationality: string,
    relationship: string,
    pass_id: string,
    has_preapproval: boolean
  ) {
    this.send(event, {
      category,
      page,
      nationality,
      relationship,
      pass_id,
      has_preapproval,
    })
  }

  sendEditPerson(
    page: string,
    category: boolean,
    num_visitors: number,
    quote_id: string,
    flow_start: string,
    holder: string,
    pass_id: string,
    has_preapproval: boolean
  ) {
    this.send('edit_person', {
      page,
      category,
      num_visitors,
      quote_id,
      flow_start,
      holder,
      pass_id,
      has_preapproval,
    })
  }

  sendViewQuote(
    category: boolean,
    num_visitors: number,
    quote_id: string,
    flow_start: string,
    preapproval_holders: string[]
  ) {
    this.send('view_quote', {
      category,
      page: 'your_quote',
      num_visitors,
      quote_id,
      flow_start,
      preapproval_holders,
    })
  }

  sendPurchaseStart(
    category: boolean,
    quote_id: string,
    total_price: string | number,
    chosen_card: string,
    flow_start: string,
    num_visitors: number,
    holders: string[],
    flg_inbound_addon: boolean,
    addons_price: number
  ) {
    this.send('start_purchase', {
      page: 'your_quote',
      category,
      quote_id,
      total_price,
      chosen_card,
      flow_start,
      num_visitors,
      holders,
      flg_inbound_addon,
      addons_price,
    })
  }

  sendExpandEventByQuote(
    category: boolean,
    num_visitors: number,
    quote_id: string,
    flow_start: string,
    holder: string,
    pass_id: string,
    has_preapproval: boolean
  ) {
    this.send('expand', {
      page: 'your_quote',
      category,
      num_visitors,
      quote_id,
      flow_start,
      holder,
      pass_id,
      has_preapproval,
    })
  }

  sendToMainPage(page: string, params?: object) {
    this.send('to_main_page', {
      page: analyticsPageMapping[page],
      ...params,
    })
  }

  sendRemoveVisitor(
    event: string,
    page: string,
    relationship: string,
    nationality: string,
    has_preapproval: boolean,
    pass_id: string,
    params?: object
  ) {
    this.send(event, {
      page: analyticsPageMapping[page],
      relationship,
      nationality,
      has_preapproval,
      pass_id,
      ...params,
    })
  }

  sendQuoteClickLink(quote_id: string) {
    this.send('click_link', {
      page: 'your_quote',
      quote_id,
      link_title: 'payment_terms_and_conditions',
    })
  }

  sendQuoteInclusiveCoversClick(event: string, quote_id: string) {
    this.send(event, {
      page: 'your_quote',
      quote_id,
      section: 'inclusive_covers',
    })
  }

  sendViewInboundAddon(quote_id: string) {
    this.send('view_inbound_addon', {
      page: 'your_quote',
      quote_id,
      item_name: 'inbound_addon_block',
      section: 'inbound_addon_block',
    })
  }

  sendToggleEventInboundAddon(quote_id: string, addon_price: number, state: boolean) {
    this.send('click_btn', {
      page: 'your_quote',
      quote_id,
      section: 'inbound_addon_block',
      addon_price,
      btn_title: 'add',
      state,
    })
  }

  sendClickPolicyWording(page: string, quote_id: string, popup_name: string) {
    this.send('click_policy_wordings', {
      category: 'visa',
      page,
      quote_id,
      popup_name,
    })
  }

  sendWebViewApp(googleId: string) {
    this.send('come_from_app', {
      google_id: googleId,
    })
  }

  sendEmailAutoFill(domain: string, page: string) {
    this.send('click_link', {
      link_title: 'email_domain',
      domain,
      page,
    })
  }

  sendClickUploader(event: string, page: string, icon_title?: string) {
    this.send(event, {
      page,
      category: 'OCR',
      icon_title,
    })
  }

  sendCloseOcrModal(event: string, page: string, popup_name: string, btn_title?: string) {
    this.send(event, {
      page,
      category: 'OCR',
      popup_name,
      btn_title,
    })
  }

  sendViewOcrBlock(page: string) {
    this.send('view_ocr_block', {
      page,
      category: 'OCR',
    })
  }

  sendClickOoredooBanner(policyId: string) {
    this.send('click_btn', {
      policy_id: policyId,
      page: 'downloading_policy',
      btn_title: 'see_plans',
    })
  }

  sendQuoteLearnMore(quote_id: string, cover_name: string) {
    this.send('click_learn_more', {
      page: analyticsPageMapping.quote,
      quote_id,
      cover_name,
    })
  }
}
